
import React, { useEffect } from 'react';
import Hero from '../components/Hero';
import Services from '../components/Services';
import Portfolio from '../components/Portfolio';
import Testimonials from '../components/Testimonials';
import Contact from '../components/Contact';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import { Leaf, ArrowDown } from 'lucide-react';
import { Link } from 'react-router-dom';

const Index = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Hero />
      
      {/* Introduction Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block mb-6">
              <Leaf className="h-12 w-12 text-pkgreen-500" />
            </span>
            <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 leading-tight">
              Creating Exceptional Landscapes for Greater Bridgeport's Finest Properties
            </h2>
            <p className="text-xl text-pkstone-700 mb-8 leading-relaxed">
              PK Landscape Associates specializes in bespoke outdoor environments for 
              Connecticut's discerning homeowners and commercial properties. From elegant estate 
              gardens in Easton and Trumbull to modern outdoor living spaces in Fairfield and Monroe, 
              we transform properties across the Greater Bridgeport area with uncompromising attention 
              to detail and superior craftsmanship.
            </p>
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-12 animate-bounce">
                <ArrowDown className="h-8 w-8 text-pkgreen-500" />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Services />
      
      {/* Latest Blog Posts Section */}
      <section className="py-20 bg-pkgreen-50 relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-pkgreen-600 font-display uppercase tracking-wider text-sm mb-2 block">
              Expert Insights
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              From Our Blog
            </h2>
            <p className="max-w-2xl mx-auto text-pkstone-700">
              Explore our latest articles for landscaping tips, design inspiration, and expert advice 
              for maintaining your outdoor spaces.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Blog Post Cards - Show latest 3 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <Link to="/blog/luxury-landscaping-trends-fairfield-county-2025" className="block">
                <img
                  src="https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?q=80&w=2069&auto=format&fit=crop"
                  alt="Luxury Landscaping Trends"
                  className="w-full h-48 object-cover"
                />
              </Link>
              <div className="p-6">
                <h3 className="font-serif font-bold text-xl mb-2 line-clamp-2">
                  <Link to="/blog/luxury-landscaping-trends-fairfield-county-2025" className="hover:text-pkgreen-600">
                    5 Luxury Landscaping Trends for Fairfield County Estates in 2025
                  </Link>
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-3">
                  Discover the latest luxury landscaping trends that are transforming high-end properties in Fairfield County.
                </p>
                <Link 
                  to="/blog/luxury-landscaping-trends-fairfield-county-2025"
                  className="text-pkgreen-600 font-medium hover:text-pkgreen-700 inline-flex items-center"
                >
                  Read More
                  <ArrowDown className="ml-2 h-4 w-4 transform rotate-270" />
                </Link>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <Link to="/blog/professional-landscape-design-property-value-connecticut" className="block">
                <img
                  src="https://images.unsplash.com/photo-1526948531399-320e7e40f0ca?q=80&w=2070&auto=format&fit=crop"
                  alt="Property Value"
                  className="w-full h-48 object-cover"
                />
              </Link>
              <div className="p-6">
                <h3 className="font-serif font-bold text-xl mb-2 line-clamp-2">
                  <Link to="/blog/professional-landscape-design-property-value-connecticut" className="hover:text-pkgreen-600">
                    How Professional Landscape Design Increases Property Value in Connecticut
                  </Link>
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-3">
                  Learn how strategic landscape design can significantly increase your Connecticut property's value.
                </p>
                <Link 
                  to="/blog/professional-landscape-design-property-value-connecticut"
                  className="text-pkgreen-600 font-medium hover:text-pkgreen-700 inline-flex items-center"
                >
                  Read More
                  <ArrowDown className="ml-2 h-4 w-4 transform rotate-270" />
                </Link>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <Link to="/blog/year-round-garden-planning-fairfield-county" className="block">
                <img
                  src="https://images.unsplash.com/photo-1555955924-a8c17aa846b7?q=80&w=2070&auto=format&fit=crop"
                  alt="Year-Round Garden Planning"
                  className="w-full h-48 object-cover"
                />
              </Link>
              <div className="p-6">
                <h3 className="font-serif font-bold text-xl mb-2 line-clamp-2">
                  <Link to="/blog/year-round-garden-planning-fairfield-county" className="hover:text-pkgreen-600">
                    Year-Round Garden Planning: A Guide for Fairfield County Homeowners
                  </Link>
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-3">
                  Create a stunning garden that offers visual interest throughout all four seasons in Connecticut.
                </p>
                <Link 
                  to="/blog/year-round-garden-planning-fairfield-county"
                  className="text-pkgreen-600 font-medium hover:text-pkgreen-700 inline-flex items-center"
                >
                  Read More
                  <ArrowDown className="ml-2 h-4 w-4 transform rotate-270" />
                </Link>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-10">
            <Link 
              to="/blog"
              className="bg-pkgreen-600 hover:bg-pkgreen-700 text-white py-3 px-8 rounded-md inline-block font-medium transition-all duration-300"
            >
              View All Articles
            </Link>
          </div>
        </div>
      </section>
      
      {/* CTA Banner */}
      <section className="py-20 bg-pkearth-900 relative">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1558293842-c0fd3db86157?q=80&w=2069&auto=format&fit=crop')] bg-cover bg-center opacity-20"></div>
        <div className="absolute inset-0 bg-pkearth-900/80"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-white">
              Ready to Transform Your Outdoor Space?
            </h2>
            <p className="text-xl text-pkearth-100 mb-10">
              Whether you're looking for a complete landscape renovation or ongoing maintenance,
              our team is here to help. Contact us today to schedule a consultation.
            </p>
            <Link
              to="/contact"
              className="bg-pkearth-100 hover:bg-pkearth-200 text-pkearth-900 py-3 px-8 rounded-md inline-block font-medium transition-all duration-300"
            >
              Request Your Consultation
            </Link>
          </div>
        </div>
      </section>
      
      <Portfolio />
      <Testimonials />
      <Contact />
      <Footer />
    </div>
  );
};

export default Index;
